import { FC } from "react";
import React, { useState, useMemo, useEffect, useRef } from "react";

interface FileWithPath extends File {
  path?: string;
}

import { AlertTriangle, ArrowLeft, Check, Download, Upload, X } from "lucide-react";

import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_module/components/ui/alert-dialog";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";

import { mapFields, policyStatusColumn } from "../../constants/mapFields";
import { useUploadLeads, useGetLeadsImportList } from "../../hooks/leads-import-queries";
import { leadsQueryKeys } from "../../services/keys";
import { leadsService } from "../../services/services";
import { detectAgentColumn, autoAssignLeadsAfterImport, fetchAgents, matchAgent } from "../../services/autoAssignmentService";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { AgentAssignmentDropdown } from "@/app/(authenticated)/lead-assignment/_modules/components/AgentAssignmentDropdown";
import { useAgentWorkload } from "@/app/(authenticated)/lead-assignment/_modules/hooks/queries";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { useToast } from "@/app/_module/hooks/use-toast";

import type { TCurrentStep } from "./uploadLeadsDialog";
import { useQueryClient } from "@tanstack/react-query";
import { useImportProgress } from "./ImportProgressContext";
import { summarizeImportResults } from "./importResultSummary";
import { isRateLimitError, getRateLimitMessage } from "@/app/_module/utils/rate-limit";

interface IMapImport {
  csvFile: FileWithPath | null;
  csvData: Record<string, unknown>[] | null;
  mappings: Record<string, string>;
  csvHeaders: string[];
  cancelImport: () => void;
  setMappings: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setCurrentStep: React.Dispatch<React.SetStateAction<TCurrentStep>>;
  onImportComplete?: () => void;
  queryClient?: any;
  // New props for headerless file detection
  fileHasHeaders?: boolean;
  detectedTypes?: Record<string, string> | null;
  // Pipeline stage to assign to imported leads
  defaultStatus?: string;
  setImportedRowCount?: (count: number) => void;
  // Lead list name for tagging imported leads
  listName?: string;
}

const CUSTOM_FIELD_VALUE = "__CUSTOM_FIELD__";

// ✅ Date parsing helper - handles Excel serial numbers and various date formats
const parseExcelDate = (value: any): string | null => {
  if (!value) return null;

  const stringValue = String(value).trim();

  try {
    // Check if it's an Excel serial number (5-digit number like 30185)
    if (typeof value === "number" || /^\d{5}$/.test(stringValue)) {
      const serial =
        typeof value === "number" ? value : parseInt(stringValue);
      const excelEpoch = new Date(1899, 11, 30);
      const date = new Date(excelEpoch.getTime() + serial * 86400000);

      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      }
    }

    // Try common date formats with separators (-, /, .)
    // Patterns: MM-DD-YYYY, DD-MM-YYYY, YYYY-MM-DD, MM/DD/YYYY, etc.
    const datePatterns = [
      // MM-DD-YYYY or MM/DD/YYYY or MM.DD.YYYY (US format)
      /^(\d{1,2})[-\/.](\d{1,2})[-\/.](\d{4})$/,
      // YYYY-MM-DD or YYYY/MM/DD (ISO format)
      /^(\d{4})[-\/.](\d{1,2})[-\/.](\d{1,2})$/,
      // DD-MM-YYYY (European format) - handled below
    ];

    // Try MM-DD-YYYY format first (US standard)
    const usMatch = stringValue.match(/^(\d{1,2})[-\/.](\d{1,2})[-\/.](\d{4})$/);
    if (usMatch) {
      const [, month, day, year] = usMatch;
      const m = parseInt(month);
      const d = parseInt(day);
      const y = parseInt(year);

      // Validate it's a reasonable date (month 1-12, day 1-31)
      if (m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 1900 && y <= 2100) {
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      }
    }

    // Try YYYY-MM-DD format (ISO)
    const isoMatch = stringValue.match(/^(\d{4})[-\/.](\d{1,2})[-\/.](\d{1,2})$/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      const m = parseInt(month);
      const d = parseInt(day);
      const y = parseInt(year);

      if (m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 1900 && y <= 2100) {
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      }
    }

    // Try MM-DD-YY format (2-digit year)
    const shortYearMatch = stringValue.match(/^(\d{1,2})[-\/.](\d{1,2})[-\/.](\d{2})$/);
    if (shortYearMatch) {
      const [, month, day, shortYear] = shortYearMatch;
      const m = parseInt(month);
      const d = parseInt(day);
      const y = parseInt(shortYear);

      // Convert 2-digit year to 4-digit (assume 00-29 = 2000-2029, 30-99 = 1930-1999)
      const fullYear = y < 30 ? 2000 + y : 1900 + y;

      if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
        return `${fullYear}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      }
    }

    // Try parsing as regular date using native Date
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
  } catch (error) {}

  // If all parsing fails, return the original value (backend will handle it)
  return stringValue;
};

// ✅ Transform CSV data with mappings applied
const transformLeadData = (
  row: Record<string, unknown>,
  mappings: Record<string, string>,
  customValues: Record<string, string>
): Record<string, any> => {
  const transformedData: Record<string, any> = {};

  // Apply field mappings
  Object.entries(mappings).forEach(([systemField, csvHeader]) => {
    // Strip prefixes from field names so they're stored cleanly
    let cleanFieldName = systemField;
    if (systemField.startsWith('unmapped.')) {
      cleanFieldName = systemField.replace('unmapped.', '');
    } else if (systemField.startsWith('customFields.')) {
      // Custom fields like "customFields.phone2" -> "phone2"
      // Backend will automatically put non-standard fields into customFields
      cleanFieldName = systemField.replace('customFields.', '');
    }

    if (csvHeader === CUSTOM_FIELD_VALUE) {
      // Use custom value
      const customValue = customValues[systemField];
      if (customValue) {
        transformedData[cleanFieldName] = customValue;
      }
    } else {
      // Map from CSV column
      let value = row[csvHeader];

      if (value !== undefined && value !== null && value !== "") {
        // Special handling for date fields
        if (
          cleanFieldName === "dob" ||
          cleanFieldName === "dateOfBirth" ||
          cleanFieldName === "birthday"
        ) {
          const parsedDate = parseExcelDate(value);
          transformedData[cleanFieldName] = parsedDate || value;
        } else {
          transformedData[cleanFieldName] = value;
        }
      }
    }
  });

  // Ensure required fields - combine firstName + lastName into name
  if (!transformedData.name) {
    if (transformedData.firstName || transformedData.lastName) {
      transformedData.name =
        `${transformedData.firstName || ""} ${transformedData.lastName || ""}`.trim();
    } else if (transformedData.firstname || transformedData.lastname) {
      transformedData.name =
        `${transformedData.firstname || ""} ${transformedData.lastname || ""}`.trim();
    }
  }

  // Map phone_home to phone if needed
  if (!transformedData.phone && transformedData.phone_home) {
    transformedData.phone = transformedData.phone_home;
  }

  // Parse a full combined address string into street / city / state / zip
  // Handles formats like:
  //   "243 E Front St, Traverse City, MI 49684, United States"
  //   "426 TRAIL SIDE DR, DEFOREST, WI, 53532"
  //   "123 Main St, Springfield, IL 62701"
  const parseFullAddress = (raw: string) => {
    // Strip trailing country name (United States, US, USA, Canada, etc.)
    const cleaned = raw.replace(/,?\s*(united states|usa|u\.s\.a\.?|us|canada|uk|united kingdom)\.?$/i, '').trim();
    const parts = cleaned.split(',').map(p => p.trim()).filter(Boolean);
    if (parts.length < 2) return null;

    const result: { address?: string; city?: string; state?: string; zipCode?: string } = {};

    // First part is always street address
    result.address = parts[0];

    if (parts.length === 2) {
      // "123 Main St, Springfield IL 62701" — city+state+zip in last part
      const lastPart = parts[1];
      const m = lastPart.match(/^(.+?)\s+([A-Z]{2})\s+(\d{5}(-\d{4})?)$/);
      if (m) {
        result.city = m[1];
        result.state = m[2];
        result.zipCode = m[3];
      } else {
        result.city = lastPart;
      }
    } else if (parts.length >= 3) {
      // Second part is city
      result.city = parts[1];
      // Third part: "MI 49684" or "MI, 49684" or just "MI"
      const stateZipPart = parts[2];
      const stateZipMatch = stateZipPart.match(/^([A-Z]{2})\s+(\d{5}(-\d{4})?)$/);
      if (stateZipMatch) {
        result.state = stateZipMatch[1];
        result.zipCode = stateZipMatch[2];
      } else {
        const stateMatch = stateZipPart.match(/^([A-Z]{2})$/);
        if (stateMatch) result.state = stateMatch[1];
        // Fourth part might be zip
        if (parts.length >= 4) {
          const zipPart = parts[3];
          if (/^\d{5}(-\d{4})?$/.test(zipPart)) result.zipCode = zipPart;
        }
      }
    }

    return result;
  };

  // Try to split a combined address stored in the address field
  if (transformedData.address && !transformedData.city && !transformedData.state && !transformedData.zipCode) {
    const raw = String(transformedData.address).trim();
    if (raw.includes(',')) {
      const parsed = parseFullAddress(raw);
      if (parsed && parsed.address) {
        transformedData.address = parsed.address;
        if (parsed.city) transformedData.city = parsed.city;
        if (parsed.state) transformedData.state = parsed.state;
        if (parsed.zipCode) transformedData.zipCode = parsed.zipCode;
      }
    }
  }

  // Also handle full address stored in city column (FollowUpBoss export format)
  if (!transformedData.address && transformedData.city) {
    const cityValue = String(transformedData.city).trim();
    if (cityValue.includes(',')) {
      const parsed = parseFullAddress(cityValue);
      if (parsed && parsed.address) {
        transformedData.address = parsed.address;
        transformedData.city = parsed.city || '';
        if (parsed.state && !transformedData.state) transformedData.state = parsed.state;
        if (parsed.zipCode && !transformedData.zipCode) transformedData.zipCode = parsed.zipCode;
      }
    }
  }

  // Handle age/DOB - DOB is the primary source of truth
  // 1. If DOB exists, calculate age from it (DOB takes priority)
  // 2. If only age exists (no DOB), use age to auto-fill/estimate DOB
  const currentYear = new Date().getFullYear();

  if (transformedData.dob) {
    // DOB exists - calculate age from it (DOB is source of truth)
    const dobDate = new Date(transformedData.dob);
    if (!isNaN(dobDate.getTime())) {
      const today = new Date();
      let calculatedAge = today.getFullYear() - dobDate.getFullYear();
      const monthDiff = today.getMonth() - dobDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dobDate.getDate())) {
        calculatedAge--;
      }
      // DOB is source of truth - always use calculated age from DOB
      if (calculatedAge >= 0 && calculatedAge <= 120) {
        transformedData.age = calculatedAge;
      }
    }
  } else if (transformedData.age !== undefined && transformedData.age !== null && transformedData.age !== "") {
    // No DOB but age exists - use age to auto-fill DOB
    const ageValue = Number(transformedData.age);

    if (!isNaN(ageValue)) {
      // Check if age looks like a birth year (1900-current year)
      if (ageValue >= 1900 && ageValue <= currentYear) {
        // It's a birth year - convert to DOB
        transformedData.dob = `${ageValue}-01-01`;
        transformedData.age = currentYear - ageValue;
      } else if (ageValue > 0 && ageValue <= 120) {
        // It's a valid age - create approximate DOB
        const birthYear = currentYear - ageValue;
        transformedData.dob = `${birthYear}-01-01`;
        transformedData.age = ageValue;
      } else if (ageValue < 0 || ageValue > 120) {
        // Invalid age - clear it
        transformedData.age = null;
      }
    }
  }

  return transformedData;
};

// ✅ Create a properly formatted CSV from transformed data
const createTransformedCSV = (
  csvData: Record<string, unknown>[],
  mappings: Record<string, string>,
  customValues: Record<string, string>,
  originalFileName?: string
): File => {
  // Transform all rows
  const transformedRows = csvData.map((row) =>
    transformLeadData(row, mappings, customValues)
  );

  // Get all unique field names from transformed data
  const allFields = new Set<string>();
  transformedRows.forEach((row) => {
    Object.keys(row).forEach((key) => allFields.add(key));
  });
  const headers = Array.from(allFields);

  // Create CSV content
  const csvLines = [
    headers.join(","), // Header row
    ...transformedRows.map((row) =>
      headers
        .map((header) => {
          const value = row[header];
          // Escape commas and quotes in values
          if (value === undefined || value === null) return "";
          const stringValue = String(value);
          if (
            stringValue.includes(",") ||
            stringValue.includes('"') ||
            stringValue.includes("\n")
          ) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        })
        .join(",")
    ),
  ];

  const csvContent = csvLines.join("\n");
  const blob = new Blob([csvContent], { type: "text/csv" });
  // Always use .csv extension since we're creating a CSV file
  // Replace any existing extension with .csv to ensure backend parses it correctly
  let fileName = "transformed_leads.csv";
  if (originalFileName) {
    // Remove the original extension and add .csv
    const baseName = originalFileName.replace(/\.[^/.]+$/, '');
    fileName = `${baseName}.csv`;
  }
  return new File([blob], fileName, { type: "text/csv" });
};

const MapImport: FC<IMapImport> = function (prop) {
  const {
    csvFile,
    csvData,
    mappings,
    csvHeaders,
    setCurrentStep,
    setMappings,
    cancelImport,
    fileHasHeaders = true,
    detectedTypes,
    defaultStatus,
    setImportedRowCount,
    listName,
  } = prop;
  const { mutate, isPending } = useUploadLeads();

  // Dumb-proofing: warn (don't block) on the review step if this exact filename
  // was imported before — the #1 source of "I uploaded 700 but only got 590"
  // confusion (most rows come back as duplicates). Shown here (not on the upload
  // screen, which auto-advances) so the user actually sees it before importing.
  const { data: priorImportData } = useGetLeadsImportList({ refetchInterval: false });
  const duplicateImportWarning = useMemo(() => {
    const selectedName = (csvFile?.name || "").trim().toLowerCase();
    if (!selectedName) return null;
    const priorImports: any[] = Array.isArray(priorImportData)
      ? priorImportData
      : ((priorImportData as any)?.data ?? []);
    const matches = priorImports.filter(
      (imp) => ((imp?.fileName || imp?.name || "") as string).trim().toLowerCase() === selectedName,
    );
    if (matches.length === 0) return null;
    const totalLeads = matches.reduce(
      (s, m) => s + (m?.resultCounts?.success || m?.processedRecords || 0),
      0,
    );
    const latest = matches.reduce((a, b) =>
      new Date(a?.createdAt || 0) > new Date(b?.createdAt || 0) ? a : b,
    );
    return { times: matches.length, totalLeads, date: latest?.createdAt as string | undefined };
  }, [csvFile, priorImportData]);

  const [customValues, setCustomValues] = useState<Record<string, string>>({});
  const [customFieldsList, setCustomFieldsList] = useState<any[]>([]);
  const [showDupConfirm, setShowDupConfirm] = useState(false);
  // Rows that are the same people as leads still in Recently Deleted (owner, 2026-11-02: delete those or don't import).
  const [trashMatches, setTrashMatches] = useState<{ ids: string[]; rows: number } | null>(null);
  const [trashBusy, setTrashBusy] = useState<"checking" | "deleting" | null>(null);

  const { startImport, updateProgress, startAssignment, completeAssignment, completeImport, failImport, coolDownImport } =
    useImportProgress();
  const queryClient = useQueryClient();

  // Track when custom fields have loaded
  const [customFieldsLoaded, setCustomFieldsLoaded] = React.useState(false);

  // Load custom fields
  React.useEffect(() => {
    const loadCustomFields = async () => {
      try {
        const customFieldsService = (
          await import("@/app/_module/services/customFieldsService")
        ).default;
        const fields = await customFieldsService.getCustomFields();
        setCustomFieldsList(fields || []);
        setCustomFieldsLoaded(true);
      } catch (error) {
        setCustomFieldsList([]);
        setCustomFieldsLoaded(true);
      }
    };
    loadCustomFields();
  }, []);

  const previewData = csvData?.slice(0, 5);

  // Auto-mapping logic with comprehensive field matching using aliases from mapFields
  // If existingMappings is provided, we keep them and only add custom field mappings
  // Uses detectedTypes for smart mapping when file has no headers
  const autoMapFields = React.useCallback((existingMappings?: Record<string, string>) => {
    if (!csvHeaders.length) return;

    const autoMappings: Record<string, string> = existingMappings ? { ...existingMappings } : {};
    const usedHeaders = new Set<string>();

    // If preserving existing mappings, mark already-mapped headers as used
    if (existingMappings) {
      Object.values(existingMappings).forEach(header => {
        if (header && header !== CUSTOM_FIELD_VALUE) {
          usedHeaders.add(header);
        }
      });
    }

    // SMART MAPPING: Use content-based mapping when detectedTypes is available
    // Check if headers look auto-generated (indicates headerless file)
    const looksLikeAutoGenerated = csvHeaders.some(h => /^Column \d+/.test(h));

    // Use smart mapping if we have detected types AND headers look auto-generated
    // This bypasses the fileHasHeaders state which may not have updated yet
    if (detectedTypes && looksLikeAutoGenerated) {
      // Map detected types to system fields - comprehensive mapping
      const typeToFieldMap: Record<string, string> = {
        'email': 'email',
        'phone': 'phone',
        'state': 'state',
        'zipCode': 'zipCode',
        'firstName': 'firstName',
        'lastName': 'lastName',
        'city': 'city',
        'dob': 'dob', // birthday column detected by age-range → map straight to DOB

        'address': 'address',
        'gender': 'gender',
        'marital': 'marital',
        'employment': 'employmentStatus',
        'smoker': 'smoker',
        'beneficiary': 'beneficiary',
        'insuranceType': 'coverageAmount', // Map to coverage or create custom field
        // NOTE: latitude/longitude are handled separately below — they only map
        // as a PAIR (a lone coordinate is useless for routing).
      };

      // Track if we found date columns to handle DOB vs created date
      let firstDateColumn: string | null = null;
      let secondDateColumn: string | null = null;
      // GPS coords — only mapped as a PAIR (both must be present).
      let latColumn: string | null = null;
      let longColumn: string | null = null;

      // First pass: use detected types
      csvHeaders.forEach((header) => {
        if (usedHeaders.has(header)) return;

        // Extract the base column name (e.g., "Column 1" from "Column 1 (Email)")
        const baseColumn = header.replace(/\s*\([^)]+\)\s*$/, '').trim();
        const detectedType = detectedTypes[baseColumn];

        if (detectedType) {
          // Special handling for date columns - first date is often created date, second is DOB
          if (detectedType === 'date') {
            if (!firstDateColumn) {
              firstDateColumn = header;
              // Don't auto-map first date (might be created date, not DOB)
            } else if (!secondDateColumn) {
              secondDateColumn = header;
              // Second date is likely DOB
              if (!autoMappings['dob']) {
                autoMappings['dob'] = header;
                usedHeaders.add(header);
              }
            }
            return;
          }

          // GPS coords — remember them; only map below if we have BOTH.
          if (detectedType === 'latitude') { if (!latColumn) latColumn = header; return; }
          if (detectedType === 'longitude') { if (!longColumn) longColumn = header; return; }

          // Map to system field if available
          const fieldKey = typeToFieldMap[detectedType];
          if (fieldKey && !autoMappings[fieldKey]) {
            autoMappings[fieldKey] = header;
            usedHeaders.add(header);
          }
        }
      });

      // If we only found one date and no DOB is mapped, use it as DOB
      if (firstDateColumn && !autoMappings['dob'] && !secondDateColumn) {
        // Check column position - if it's late in the file (after column 8), it's likely DOB
        const dateCol = firstDateColumn as string;
        const colMatch = dateCol.match(/Column (\d+)/);
        if (colMatch && parseInt(colMatch[1]) > 8) {
          autoMappings['dob'] = dateCol;
          usedHeaders.add(dateCol);
        }
      }

      // GPS coords: only map when we have BOTH latitude AND longitude columns —
      // a lone coordinate can't route a lead, so we leave a solo one unmapped.
      if (latColumn && longColumn) {
        if (!autoMappings['latitude']) { autoMappings['latitude'] = latColumn; usedHeaders.add(latColumn); }
        if (!autoMappings['longitude']) { autoMappings['longitude'] = longColumn; usedHeaders.add(longColumn); }
      }
    }

    // A policy file's "Status" is the policy's, not the lead's stage: keep it as "Policy Status".
    const policyStatus = policyStatusColumn(csvHeaders);
    if (policyStatus && !usedHeaders.has(policyStatus) && !autoMappings['unmapped.Policy Status']) {
      autoMappings['unmapped.Policy Status'] = policyStatus;
      usedHeaders.add(policyStatus);
    }

    // First pass: match against field aliases (case-insensitive)
    // Track if firstName/lastName are found to skip mapping 'name' field
    let foundFirstName = false;
    let foundLastName = false;

    // Pre-scan: For fields with priorityAliases (like notes), check if a priority column exists
    // This ensures "Notes" is picked over "Message" even if Message appears first in CSV
    const priorityMappings: Record<string, string> = {};
    for (const field of mapFields) {
      const priorityAliases = (field as any).priorityAliases || [];
      if (priorityAliases.length > 0) {
        // Look for a priority alias match in headers
        for (const header of csvHeaders) {
          const normalizedHeader = header.toLowerCase().replace(/[^a-z0-9]/g, "");
          const isPriority = priorityAliases.some((alias: string) => {
            const normalizedAlias = alias.toLowerCase().replace(/[^a-z0-9]/g, "");
            return normalizedHeader === normalizedAlias;
          });
          if (isPriority) {
            priorityMappings[field.key] = header;
            break;
          }
        }
      }
    }

    csvHeaders.forEach((header) => {
      if (usedHeaders.has(header)) return;

      // Normalize header for comparison - lowercase and remove special chars
      const normalizedHeader = header.toLowerCase().replace(/[^a-z0-9]/g, "");

      // Check against each field's aliases
      for (const field of mapFields) {
        if (autoMappings[field.key]) {
          continue;
        }

        // If this field has a priority mapping, only accept that specific header
        if (priorityMappings[field.key] && priorityMappings[field.key] !== header) {
          continue;
        }

        const fieldAliases = (field as any).aliases || [];
        const matchingAlias = fieldAliases.find((alias: string) => {
          const normalizedAlias = alias.toLowerCase().replace(/[^a-z0-9]/g, "");
          return normalizedHeader === normalizedAlias;
        });

        if (matchingAlias) {
          // Track firstName/lastName for later
          if (field.key === 'firstName') foundFirstName = true;
          if (field.key === 'lastName') foundLastName = true;

          autoMappings[field.key] = header;
          usedHeaders.add(header);
          break;
        }
      }
    });

    // If we found firstName or lastName, remove any 'name' mapping
    // (name will be auto-generated from firstName + lastName)
    if (foundFirstName || foundLastName) {
      if (autoMappings['name']) {
        const nameHeader = autoMappings['name'];
        delete autoMappings['name'];
        usedHeaders.delete(nameHeader);
      }
    }

    // Second pass: try EXACT matching against mapFields labels and keys only
    // (removed fuzzy matching that was causing false positives)
    csvHeaders.forEach((header) => {
      if (usedHeaders.has(header)) return;

      const normalizedHeader = header.toLowerCase().replace(/[^a-z0-9]/g, "");

      const bestMatch = mapFields.find((field) => {
        if (autoMappings[field.key]) return false; // Already mapped

        const normalizedField = field.label.toLowerCase().replace(/[^a-z0-9]/g, "");
        const normalizedKey = field.key.toLowerCase().replace(/[^a-z0-9]/g, "");

        // Only exact matches - no fuzzy matching
        return (
          normalizedHeader === normalizedField ||
          normalizedHeader === normalizedKey
        );
      });

      if (bestMatch) {
        autoMappings[bestMatch.key] = header;
        usedHeaders.add(header);
      }
    });

    // Third pass: auto-map user's custom fields (exact match only)
    csvHeaders.forEach((header) => {
      if (usedHeaders.has(header)) return;

      const normalizedHeader = header.toLowerCase().replace(/[^a-z0-9]/g, "");

      // Check against user's custom fields - exact matches only
      const matchingCustomField = customFieldsList.find((cf) => {
        const normalizedName = cf.name.toLowerCase().replace(/[^a-z0-9]/g, "");
        const normalizedLabel = (cf.label || cf.name).toLowerCase().replace(/[^a-z0-9]/g, "");

        return (
          normalizedHeader === normalizedName ||
          normalizedHeader === normalizedLabel
        );
      });

      if (matchingCustomField) {
        const fieldKey = `customFields.${matchingCustomField.name}`;
        if (!autoMappings[fieldKey]) {
          autoMappings[fieldKey] = header;
          usedHeaders.add(header);
        }
      }
    });

    // Fourth pass: auto-map any remaining unmapped CSV columns as "unmapped" custom data
    // This ensures all CSV columns appear in the UI with boxes
    csvHeaders.forEach((header) => {
      if (usedHeaders.has(header)) return;

      // Map it as an unmapped column that will be imported as custom data
      const fieldKey = `unmapped.${header}`;
      autoMappings[fieldKey] = header;
      usedHeaders.add(header);
    });

    // Apply mappings
    setMappings(autoMappings);
  }, [csvHeaders, setMappings, customFieldsList, fileHasHeaders, detectedTypes]);

  // Get all available fields (separated)
  // Hide "Name" field if firstName or lastName are already mapped (from smart mapping or header matching)
  // This prevents showing the confusing dual-mapping UI when we already have firstName/lastName mapped
  const getStandardFields = () => {
    // Filter out firstName and lastName as separate fields - they're handled by the Name field's dual-mapping UI
    // This prevents duplicate mapping options and keeps the UI cleaner
    return mapFields.filter(f => f.key !== 'firstName' && f.key !== 'lastName');
  };

  const getCustomFields = () => {
    return customFieldsList.map((cf) => ({
      key: `customFields.${cf.name}`,
      label: cf.label || cf.name,
      required: cf.required || false,
      description: cf.description || `Custom field: ${cf.label || cf.name}`,
      isCustomField: true,
    }));
  };

  const getAllFields = () => {
    return [...getStandardFields(), ...getCustomFields()];
  };

  // Get CSV columns that are mapped as "unmapped" (additional data columns)
  // These are columns from the CSV that don't match any standard or custom field
  const getUnmappedCsvColumns = () => {
    // Find all mappings with "unmapped." prefix. The saved name is usually the column's
    // own, but not always ("status" → "Policy Status").
    return Object.entries(mappings)
      .filter(([key, value]) => key.startsWith('unmapped.') && value)
      .map(([key, value]) => ({ fieldKey: key, header: value, target: key.slice('unmapped.'.length) }));
  };

  // Validation
  const validateMappings = () => {
    const allFields = getAllFields();
    const requiredFields = allFields.filter((f) => f.required);
    const missingRequired = requiredFields.filter(
      (f) => !mappings[f.key] || mappings[f.key] === ""
    );
    return missingRequired;
  };

  // ── IMPORT + ASSIGN ─────────────────────────────────────────────────────
  //
  // Picking the agent here removes a whole second trip: import, then find the
  // list on the assignment page, then assign. It matters more now that
  // DriverIDFK is blacklisted — vendor files no longer name an agent, so
  // everything they contain lands unassigned and someone has to place it.
  //
  // A file that DOES carry a real agent column still wins: this is an override
  // for files that don't, and the preview panel below says which case you're in.
  const { toast } = useToast();
  const [assignToAgentId, setAssignToAgentId] = useState<string>("");
  // The completion handler runs long after this render — a ref keeps it reading
  // the current pick instead of the value captured when the import started.
  const assignToAgentIdRef = useRef<string>("");
  useEffect(() => { assignToAgentIdRef.current = assignToAgentId; }, [assignToAgentId]);
  // The picker's own cached list, for the picked agent's name in the progress box.
  const { data: agentWorkloadData } = useAgentWorkload();

  // ── AGENT ASSIGNMENT PREVIEW ────────────────────────────────────────────
  // Resolves the file's agent column against the real roster so the panel below
  // the mapping table can state, before anything is written, exactly who this
  // import will assign to. Runs once per file; sampling is capped because the
  // point is the distinct set of agent names, not a full scan.
  const [agentPreview, setAgentPreview] = useState<{
    column: string | null;
    filled: number;
    sampled: number;
    resolved: Array<{ value: string; agentName: string | null }>;
  } | null>(null);

  useEffect(() => {
    if (!csvData || csvData.length === 0 || !csvHeaders || csvHeaders.length === 0) {
      setAgentPreview(null);
      return;
    }
    let cancelled = false;

    (async () => {
      const column = detectAgentColumn(csvHeaders, csvData as any);
      const sample = (csvData as any[]).slice(0, 500);
      const values = column
        ? Array.from(
            new Set(
              sample
                .map((r) => String((r as Record<string, unknown>)?.[column] ?? '').trim())
                .filter(Boolean),
            ),
          )
        : [];

      // Roster lookup is best-effort — a failed fetch shows the column and the
      // raw values rather than nothing, which is still enough to spot a wrong file.
      const agents = column && values.length > 0 ? await fetchAgents().catch(() => []) : [];
      if (cancelled) return;

      setAgentPreview({
        column,
        filled: values.length ? sample.filter((r) => String((r as Record<string, unknown>)?.[column!] ?? '').trim()).length : 0,
        sampled: sample.length,
        resolved: values.slice(0, 8).map((value) => ({
          value,
          agentName: agents.length ? (matchAgent(value, agents)?.agentName ?? null) : null,
        })),
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [csvData, csvHeaders]);

  const handleImport = async (confirmed = false, trashCleared = false) => {
    if (!csvData || !csvFile) {
      return;
    }

    // Validate required field mappings
    const missingRequired = validateMappings();
    if (missingRequired.length > 0) {
      alert(
        `Missing required fields: ${missingRequired.map((f) => f.label).join(", ")}`
      );
      return;
    }

    // Dupe guard: if this exact file was imported before, make them confirm once.
    if (duplicateImportWarning && !confirmed) {
      setShowDupConfirm(true);
      return;
    }

    if (!trashCleared) {
      setTrashBusy("checking");
      const found = await leadsService
        .findRecentlyDeletedMatches(
          csvData.map((row) => {
            const t = transformLeadData(row, mappings, customValues);
            return { name: t.name, phone: t.phone, email: t.email };
          }),
        )
        // The check is a courtesy: if it fails, the import runs as it always did.
        .catch(() => null)
        .finally(() => setTrashBusy(null));
      if (found?.ids.length) {
        setTrashMatches(found);
        return;
      }
    }

    const importId = `import_${Date.now()}`;
    const fileName = csvFile.name;
    const totalRecords = csvData.length;

    // Capture data for auto-assignment before component might unmount
    // These will be used in the polling callback after import completes
    const originalCsvData = [...csvData];
    const originalCsvHeaders = [...csvHeaders];
    const originalMappings = { ...mappings };

    // Save the row count before dialog closes and csvData gets reset
    setImportedRowCount?.(totalRecords);

    // Start import progress tracking. The box says who this assigns to from the first second, so a
    // missed pick shows before the import ends instead of finishing as a plain "Complete".
    const pickedId = assignToAgentIdRef.current;
    const pickedName = pickedId
      ? ((agentWorkloadData?.agents || []) as any[]).find((a) => a.agentId === pickedId)?.agentName || "the agent you picked"
      : null;
    startImport(importId, fileName, totalRecords, pickedId ? pickedName : agentPreview?.column ? undefined : null);

    try {
      const transformedFile = createTransformedCSV(
        csvData,
        mappings,
        customValues,
        csvFile.name // Pass the original file name
      );

      // Close the dialog
      setTimeout(() => {
        cancelImport();
      }, 500);

      // Upload transformed file to backend
      const result = await leadsService.bulkImportLeads({
        file: transformedFile,
        importName: listName || `Import ${new Date().toLocaleString()}`,
        createImmediately: true,
        defaultStatus,
        listName,
      });

      if (!result.success || !result.importId) {
        throw new Error("Failed to start import - no import ID returned");
      }

      const backendImportId = result.importId;

      updateProgress(
        importId,
        0,
        `Import queued! Processing ${totalRecords} records...`
      );

      // Poll for import status
      // For large imports (20k+ leads), we need longer timeout
      // 900 polls × 2 seconds = 30 minutes max
      let pollCount = 0;
      const maxPolls = 900;

      const pollInterval = setInterval(async () => {
        try {
          pollCount++;

          if (pollCount >= maxPolls) {
            clearInterval(pollInterval);
            failImport(importId, "Import timed out after 30 minutes. Please try with smaller batches.");
            return;
          }

          const status = await leadsService.getImportStatus(backendImportId);

          if (status.errorMessage) {
          }

          const processed = status.processedRecords || 0;
          const total = status.totalRecords || totalRecords;
          const percentage =
            total > 0 ? Math.round((processed / total) * 100) : 0;

          updateProgress(
            importId,
            processed,
            `Processing: ${processed}/${total} records (${percentage}%)`
          );

          if (
            status.status === "completed" ||
            status.status === "ready_for_activation"
          ) {
            clearInterval(pollInterval);

            try {
              // Fetch full import details
              const importDetails = await leadsService.getLeadsImportById(
                backendImportId,
                {
                  includeFullData: true,
                }
              );

              const results = importDetails.results || [];

              // The agent-column auto-assign further down still works from these.
              const successfulResults = results.filter(
                (r) =>
                  (r.status === "success" || r.status === "completed") &&
                  r.action !== "skipped"
              );
              const finalCreated = successfulResults.length;

              // New / already in your leads / failed. The box said "1 leads imported,
              // Total Rows 1" for a 250-row file whose other 249 people were already in
              // the system, and listed those 249 as red errors.
              const summary = summarizeImportResults(results);
              completeImport(
                importId,
                {
                  leadsCreated: summary.created.length,
                  alreadyInLeads: summary.alreadyInLeads.length,
                  repeatsInFile: summary.repeatsInFile.length,
                  totalRows: summary.total,
                  householdsCreated: 0,
                  householdMembersCreated: 0,
                  errors: summary.failed.length,
                },
                [
                  ...summary.failed.map((r) => ({
                    lead: r.rawDataSnapshot,
                    error: r.message || "Unknown error",
                  })),
                  ...summary.alreadyInLeads.map((r) => ({
                    lead: r.rawDataSnapshot,
                    error: r.message || "Already in your leads",
                    kind: "duplicate" as const,
                  })),
                ]
              );

              // EXPLICIT PICK WINS. If the user chose an agent on the mapping
              // step, everyone in this file goes to them — no column detection,
              // no name matching, nothing to guess wrong. This is the path for
              // vendor files that carry no usable agent column.
              //
              // That includes people already in your leads (merged and duplicate
              // rows), sent with onlyUnassigned so the server leaves a lead another
              // agent holds with that agent. Only created rows used to go: a
              // re-upload of 250 people Moe Ray had just unassigned gave the agent 1
              // (2026-10-13), and merged rows were taken off whoever had them.
              const chosenAgentId = assignToAgentIdRef.current;
              const toAssign = summary.createdIds.length + summary.existingIds.length;
              if (chosenAgentId && toAssign > 0) {
                // ⚠️ CHUNK AT 2,000. The backend rejects any /lead-assignment/assign
                // call carrying more than MAX_BULK_ASSIGN (2,000) ids with a 400.
                // Sending a whole import in one request therefore assigned NOTHING
                // on any list above a thousand leads — and because the failure was
                // swallowed into a widget status, the import still reported
                // "completed" and the leads showed up unassigned with no error
                // anywhere. Reported on a 2,390-lead list, 2026-09-01.
                //
                // Sequential, not parallel: same reason the assignment page does it
                // this way — it keeps lock contention down and makes a partial
                // failure resumable instead of all-or-nothing.
                const ASSIGN_CHUNK = 2000;
                const batches: Array<{ ids: string[]; onlyUnassigned: boolean }> = [];
                const addBatches = (ids: string[], onlyUnassigned: boolean) => {
                  for (let i = 0; i < ids.length; i += ASSIGN_CHUNK) {
                    batches.push({ ids: ids.slice(i, i + ASSIGN_CHUNK), onlyUnassigned });
                  }
                };
                addBatches(summary.createdIds, false);
                addBatches(summary.existingIds, true);
                let assignedOk = 0;
                let assignFailed = 0;
                let alreadyTheirs = 0;
                let heldByOthers = 0;
                let assignError: string | null = null;

                if (typeof startAssignment === 'function') {
                  startAssignment(importId, 'selected agent');
                }
                for (const batch of batches) {
                  try {
                    // handleApiCall so a non-2xx actually throws instead of being
                    // read as success — that is what hid the 400.
                    const data: any = await handleApiCall(async () => await api.post({
                      url: getApiUrl(endpoint.leadAssignment.assignLead),
                      auth: true,
                      body: { leadIds: batch.ids, agentId: chosenAgentId, ...(batch.onlyUnassigned && { onlyUnassigned: true }) },
                      // per-request budget for a 2,000-lead chunk; Cloudflare stops any request at 100s
                      options: { timeout: 90_000 },
                    }));
                    assignedOk += Number(data?.assignedCount ?? batch.ids.length) || 0;
                    alreadyTheirs += Number(data?.skippedCount) || 0;
                    heldByOthers += Number(data?.heldByOthersCount) || 0;
                  } catch (e: any) {
                    assignFailed += batch.ids.length;
                    // Keep the backend's real sentence — it explains capacity caps
                    // ("… is on the trial plan (cap: 1,000 leads)") rather than a
                    // generic failure.
                    assignError = e?.message || assignError;
                  }
                }

                if (typeof completeAssignment === 'function') {
                  completeAssignment(importId, {
                    agentColumn: 'selected agent',
                    attempted: toAssign,
                    successful: assignedOk,
                    failed: assignFailed,
                    skipped: alreadyTheirs + heldByOthers,
                    alreadyTheirs,
                    heldByOthers,
                  });
                }
                // Say so out loud. The leads exist either way, but silently not
                // assigning them is the bug we are fixing.
                if (assignFailed > 0) {
                  toast({
                    title: assignedOk > 0 ? "Some leads were not assigned" : "Leads imported but not assigned",
                    description:
                      (assignError ? assignError + " " : "") +
                      `${assignedOk.toLocaleString()} of ${toAssign.toLocaleString()} assigned. ` +
                      `You can assign the rest from the Lead Assignment page.`,
                    variant: "destructive",
                  });
                }
              } else if (originalCsvData && originalCsvData.length > 0 && finalCreated > 0) {
                // Rows passed so a blank `AGENT` column can't beat a populated one.
                const agentColumn = detectAgentColumn(originalCsvHeaders, originalCsvData);
                if (agentColumn) {
                  // Get the IDs of successfully imported leads
                  const importedLeadIds = successfulResults
                    .filter((r: any) => r.entityId)
                    .map((r: any) => String(r.entityId));

                  // Run auto-assignment and WAIT for it to complete
                  // This ensures assignments happen before user navigates away
                  try {
                    // Update widget to show "Assigning" status (with defensive check for hot reload)
                    if (typeof startAssignment === 'function') {
                      startAssignment(importId, agentColumn);
                    }

                    const assignResult = await autoAssignLeadsAfterImport(
                      originalCsvData,
                      originalCsvHeaders,
                      importedLeadIds,
                      originalMappings
                    );

                    // Update widget with assignment stats (with defensive check for hot reload)
                    if (typeof completeAssignment === 'function') {
                      completeAssignment(importId, {
                        agentColumn: assignResult.agentColumn,
                        attempted: assignResult.attempted,
                        successful: assignResult.successful,
                        failed: assignResult.failed,
                        skipped: assignResult.skipped,
                      });
                    }

                    if (assignResult.successful > 0) {
                      // Invalidate queries to refresh UI
                      queryClient.invalidateQueries({ queryKey: ["leads"] });
                      queryClient.invalidateQueries({ queryKey: ["unassignedLeads"] });
                      queryClient.invalidateQueries({ queryKey: ["agentWorkload"] });
                    }
                  } catch (assignError) {
                    console.error('[AutoAssign] Auto-assignment failed:', assignError);
                    // Complete assignment with no successes - widget will still show completion
                    if (typeof completeAssignment === 'function') {
                      completeAssignment(importId, {
                        agentColumn: agentColumn,
                        attempted: 0,
                        successful: 0,
                        failed: 0,
                        skipped: 0,
                      });
                    }
                  }
                }
              }
            } catch (error) {

              const created = status.resultCounts?.created || 0;
              const failed = (status.totalRecords || 0) - created;

              completeImport(
                importId,
                {
                  leadsCreated: created,
                  householdsCreated: 0,
                  householdMembersCreated: 0,
                  errors: failed,
                },
                []
              );
            }

            await queryClient.invalidateQueries({ queryKey: ["leads"] });
            await queryClient.invalidateQueries({ queryKey: ["households"] });
          } else if (status.status === "failed") {
            clearInterval(pollInterval);
            failImport(importId, status.errorMessage || "Import failed");
          }
        } catch (error) {
          if (pollCount >= 10) {
            clearInterval(pollInterval);
            failImport(
              importId,
              error instanceof Error
                ? error.message
                : "Failed to check import status"
            );
          }
        }
      }, 2000);
    } catch (error) {
      // bulkImportLeads throws the result of transformErrorResponse() — a plain
      // TErrorResponse object, not an Error instance. The previous
      // `instanceof Error` check failed, so the real reason
      // (e.g. "Import name must be at least 2 characters long") was swallowed
      // and every failure showed the generic "Failed to start import" toast.
      // CSV imports are rate-limited (backend → HTTP 429, max 10 per 5 min per
      // user). That's a COOLDOWN, not a failure — show the amber "cooling down"
      // state in the progress box instead of a red "failed".
      if (isRateLimitError(error)) {
        coolDownImport(importId, getRateLimitMessage(error));
        return;
      }

      const reason =
        (error as { message?: unknown })?.message ??
        (error instanceof Error ? error.message : undefined);
      failImport(
        importId,
        typeof reason === "string" && reason.trim()
          ? reason
          : "Failed to start import"
      );
    }
  };
  const handleFieldMapping = (fieldKey: string, csvHeader: string) => {
    if (csvHeader === "" || csvHeader === "unmapped") {
      setMappings((prev) => {
        const newMappings = { ...prev };
        delete newMappings[fieldKey];
        return newMappings;
      });
      setCustomValues((prev) => {
        const newCustomValues = { ...prev };
        delete newCustomValues[fieldKey];
        return newCustomValues;
      });
    } else {
      setMappings((prev) => ({
        ...prev,
        [fieldKey]: csvHeader,
      }));
      if (csvHeader !== CUSTOM_FIELD_VALUE) {
        setCustomValues((prev) => {
          const newCustomValues = { ...prev };
          delete newCustomValues[fieldKey];
          return newCustomValues;
        });
      }
    }
  };

  const clearMapping = (fieldKey: string) => {
    setMappings((prev) => {
      const newMappings = { ...prev };
      delete newMappings[fieldKey];
      return newMappings;
    });
    setCustomValues((prev) => {
      const newCustomValues = { ...prev };
      delete newCustomValues[fieldKey];
      return newCustomValues;
    });
  };

  const handleCustomValueChange = (fieldKey: string, value: string) => {
    setCustomValues((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));

    setMappings((prev) => ({
      ...prev,
      [fieldKey]: value.trim() || CUSTOM_FIELD_VALUE,
    }));
  };

  // Track if initial auto-mapping has been done
  const [initialAutoMapDone, setInitialAutoMapDone] = React.useState(false);
  // Track if we've done smart mapping with detectedTypes
  const [smartMappingDone, setSmartMappingDone] = React.useState(false);

  // Run initial auto-mapping when csvHeaders are available
  // For headerless files, also wait for detectedTypes to be available
  React.useEffect(() => {
    if (csvHeaders.length > 0 && !initialAutoMapDone) {
      // Detect if headers look auto-generated (e.g., "Column 1 (Date)" or "Column 1")
      // This indicates a headerless file that needs detectedTypes for smart mapping
      const looksLikeAutoGeneratedHeaders = csvHeaders.some(h => /^Column \d+/.test(h));

      // If headers look auto-generated, wait for detectedTypes before running auto-mapping
      // This handles the race condition where fileHasHeaders state hasn't updated yet
      if (looksLikeAutoGeneratedHeaders && !detectedTypes) {
        return;
      }

      autoMapFields();
      setInitialAutoMapDone(true);
      if (detectedTypes) {
        setSmartMappingDone(true);
      }
    }
  }, [csvHeaders, autoMapFields, initialAutoMapDone, fileHasHeaders, detectedTypes, smartMappingDone]);

  // Separate effect: Re-run smart mapping if detectedTypes arrives AFTER initial mapping
  // This handles the race condition where standard mapping runs before detectedTypes is available
  React.useEffect(() => {
    const looksLikeAutoGeneratedHeaders = csvHeaders.some(h => /^Column \d+/.test(h));

    // If we've done initial mapping but NOT smart mapping, and now have detectedTypes
    if (initialAutoMapDone && !smartMappingDone && detectedTypes && looksLikeAutoGeneratedHeaders) {
      autoMapFields();
      setSmartMappingDone(true);
    }
  }, [csvHeaders, detectedTypes, initialAutoMapDone, smartMappingDone, autoMapFields]);

  // Re-run auto-mapping when custom fields finish loading (to catch custom field matches)
  // Pass current mappings to preserve them and only add custom fields
  React.useEffect(() => {
    if (customFieldsLoaded && customFieldsList.length > 0 && csvHeaders.length > 0 && initialAutoMapDone) {
      // Re-run auto-mapping to include custom fields, preserving existing standard mappings
      autoMapFields(mappings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFieldsLoaded, customFieldsList.length, csvHeaders.length, initialAutoMapDone]);

  const allFields = getAllFields();
  const missingRequired = validateMappings();

  return (
    <div className="space-y-6">
      {/* Dumb-proof: already-imported-this-file warning (non-blocking) */}
      {duplicateImportWarning && (
        <div className="flex items-start gap-2 p-3 rounded-lg border border-red-200 bg-red-50 text-red-800">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p className="text-xs leading-relaxed">
            <span className="font-medium">
              You already imported a file named &quot;{csvFile?.name}&quot;
              {duplicateImportWarning.times > 1 ? ` ${duplicateImportWarning.times} times` : ""}
            </span>
            {duplicateImportWarning.date
              ? ` (last on ${new Date(duplicateImportWarning.date).toLocaleDateString()}, ${duplicateImportWarning.totalLeads.toLocaleString()} leads added).`
              : "."}{" "}
            Re-importing is fine — rows that already exist are skipped automatically as duplicates — but you may not get many new leads. This upload is kept as its own separate list.
          </p>
        </div>
      )}

      {/* Header — min-w-0 + shrink-0 keep the Auto Map button INSIDE the card
          at phone width instead of overflowing past the edge */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold">Map CSV Fields</h2>
          <p className="text-sm text-muted-foreground">
            Map your CSV columns to lead fields. Data will be transformed before
            import.
          </p>
        </div>
        <Button
          onClick={() => autoMapFields()}
          variant="outline"
          size="sm"
          className="flex flex-shrink-0 items-center gap-2"
        >
          <Download className="h-4 w-4" />
          Auto Map
        </Button>
      </div>

      {/* Warning for headerless files */}
      {!fileHasHeaders && (
        <Alert className="border-amber-300 bg-amber-50">
          <AlertDescription className="text-amber-800">
            <strong>No column headers detected.</strong> This file appears to have data in the first row instead of column names.
            We've analyzed the data to suggest column types (shown in parentheses).
            Please manually map each column to the correct field below, or use the detected types as a guide.
          </AlertDescription>
        </Alert>
      )}

      {/* Field Mapping */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Available Fields */}
        <div className="space-y-4">
          <h3 className="font-medium">Available Lead Fields</h3>
          <div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
            <div className="space-y-4">
              {/* Standard Fields Section */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Standard Fields
                </h4>
                {getStandardFields().map((field: any) => {
                  const isCustomField =
                    mappings[field.key] === CUSTOM_FIELD_VALUE ||
                    customValues[field.key];

                  // Check if this field has dual mapping (like Name = First + Last)
                  const hasDualMapping = field.hasDualMapping === true;
                  const secondaryKey = field.secondaryKey || 'lastName';
                  const hasFirstName = mappings['firstName'];
                  const hasLastName = mappings['lastName'];
                  const hasDualMappingActive = hasDualMapping && (hasFirstName || hasLastName);

                  // Special layout for dual mapping (Name field)
                  if (hasDualMapping) {
                    return (
                      <div
                        key={field.key}
                        className="p-2 rounded-lg border bg-card"
                      >
                        {/* Label and mapping status on top - centered */}
                        <div className="mb-2 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <span className="font-medium">
                              {field.label}
                              {field.required && (
                                <span className="text-red-500">*</span>
                              )}
                            </span>
                            {(hasDualMappingActive || mappings[field.key]) && (
                              <Badge variant="secondary" className="text-xs">
                                Mapped
                              </Badge>
                            )}
                          </div>
                          {hasDualMappingActive && (
                            <p className="text-xs text-blue-600">
                              → {hasFirstName || '(none)'} + {hasLastName || '(none)'}
                            </p>
                          )}
                          {mappings[field.key] && !hasDualMappingActive && (
                            <p className="text-xs text-blue-600">
                              → {mappings[field.key]}
                            </p>
                          )}
                        </div>
                        {/* Dual mapping controls */}
                        <div className="flex flex-col gap-2 w-full">
                          {/* First row: First Name + Last Name */}
                          <div className="flex items-end gap-2 w-full">
                            <div className="flex flex-col flex-1 min-w-0">
                              <span className="text-[10px] text-muted-foreground">{field.primaryLabel || 'First'}</span>
                              <select
                                value={mappings['firstName'] || ""}
                                onChange={(e) =>
                                  handleFieldMapping('firstName', e.target.value)
                                }
                                className="text-xs rounded border px-2 py-1 w-full min-w-0"
                              >
                                <option value="">First Name</option>
                                {csvHeaders.map((header) => (
                                  <option key={header} value={header}>
                                    {header}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <span className="text-xs text-muted-foreground pb-1 shrink-0">+</span>
                            <div className="flex flex-col flex-1 min-w-0">
                              <span className="text-[10px] text-muted-foreground">{field.secondaryLabel || 'Last'}</span>
                              <select
                                value={mappings['lastName'] || ""}
                                onChange={(e) =>
                                  handleFieldMapping('lastName', e.target.value)
                                }
                                className="text-xs rounded border px-2 py-1 w-full min-w-0"
                              >
                                <option value="">Last Name</option>
                                {csvHeaders.map((header) => (
                                  <option key={header} value={header}>
                                    {header}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          {/* Second row: or Full Name centered */}
                          <div className="flex flex-col items-center w-full">
                            <span className="text-[10px] text-muted-foreground">or Full Name</span>
                            <select
                              value={mappings[field.key] || ""}
                              onChange={(e) => {
                                handleFieldMapping(field.key, e.target.value);
                                // Clear first/last name if mapping full name
                                if (e.target.value) {
                                  setMappings((prev) => {
                                    const newMappings = { ...prev };
                                    delete newMappings['firstName'];
                                    delete newMappings['lastName'];
                                    return { ...newMappings, [field.key]: e.target.value };
                                  });
                                }
                              }}
                              className="text-xs rounded border px-2 py-1 w-full min-w-0"
                            >
                              <option value="">Full Name</option>
                              {csvHeaders.map((header) => (
                                <option key={header} value={header}>
                                  {header}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={field.key}
                      className="flex items-center justify-between p-2 rounded-lg border bg-card"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {field.label}
                            {field.required && (
                              <span className="text-red-500">*</span>
                            )}
                          </span>
                          {mappings[field.key] && (
                            <Badge variant="secondary" className="text-xs">
                              {isCustomField ? "Custom" : "Mapped"}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {field.description}
                        </p>
                        {mappings[field.key] && !isCustomField && (
                          <p className="text-xs text-blue-600">
                            → {mappings[field.key]}
                          </p>
                        )}
                        {isCustomField && customValues[field.key] && (
                          <p className="text-xs text-green-600">
                            → Custom: {customValues[field.key]}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {isCustomField ? (
                          <input
                            autoFocus
                            type="text"
                            placeholder="Enter custom field name..."
                            value={customValues[field.key] || ""}
                            onChange={(e) =>
                              handleCustomValueChange(field.key, e.target.value)
                            }
                            className="text-xs rounded border px-2 py-1 w-40"
                          />
                        ) : (
                          <div className="flex flex-col gap-1">
                            <select
                              value={mappings[field.key] || ""}
                              onChange={(e) =>
                                handleFieldMapping(field.key, e.target.value)
                              }
                              className="text-xs rounded border px-2 py-1 w-full min-w-[140px]"
                            >
                              <option value="">Select CSV column</option>
                              {csvHeaders.map((header) => (
                                <option key={header} value={header}>
                                  {header}
                                </option>
                              ))}
                              <option value={CUSTOM_FIELD_VALUE}>
                                Custom Field
                              </option>
                            </select>
                            {mappings[field.key] &&
                              mappings[field.key] !== CUSTOM_FIELD_VALUE && (
                                <input
                                  type="text"
                                  placeholder="Or type column name..."
                                  value={mappings[field.key] || ""}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    if (newValue.trim()) {
                                      setMappings((prev) => ({
                                        ...prev,
                                        [field.key]: newValue,
                                      }));
                                    }
                                  }}
                                  className="text-xs rounded border px-2 py-1 w-full min-w-[140px]"
                                  title="Edit the CSV column name if auto-mapping is incorrect"
                                />
                              )}
                          </div>
                        )}
                        {(mappings[field.key] || hasDualMappingActive) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              clearMapping(field.key);
                              if (hasDualMapping) {
                                clearMapping('firstName');
                                clearMapping('lastName');
                              }
                            }}
                            className="h-6 w-6 p-0"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Custom Fields Section */}
              {getCustomFields().length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide border-t pt-4">
                    Custom Fields
                  </h4>
                  {getCustomFields().map((field) => {
                    const isCustomField =
                      mappings[field.key] === CUSTOM_FIELD_VALUE ||
                      customValues[field.key];

                    return (
                      <div
                        key={field.key}
                        className="flex items-center justify-between p-2 rounded-lg border bg-card border-purple-200"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {field.label}
                              {field.required && (
                                <span className="text-red-500">*</span>
                              )}
                            </span>
                            {mappings[field.key] && (
                              <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">
                                {isCustomField ? "Custom" : "Mapped"}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {field.description}
                          </p>
                          {mappings[field.key] && !isCustomField && (
                            <p className="text-xs text-purple-600">
                              → {mappings[field.key]}
                            </p>
                          )}
                          {isCustomField && customValues[field.key] && (
                            <p className="text-xs text-green-600">
                              → Custom: {customValues[field.key]}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {isCustomField ? (
                            <input
                              autoFocus
                              type="text"
                              placeholder="Enter custom field name..."
                              value={customValues[field.key] || ""}
                              onChange={(e) =>
                                handleCustomValueChange(field.key, e.target.value)
                              }
                              className="text-xs rounded border px-2 py-1 w-40"
                            />
                          ) : (
                            <div className="flex flex-col gap-1">
                              <select
                                value={mappings[field.key] || ""}
                                onChange={(e) =>
                                  handleFieldMapping(field.key, e.target.value)
                                }
                                className="text-xs rounded border px-2 py-1 w-full min-w-[140px]"
                              >
                                <option value="">Select CSV column</option>
                                {csvHeaders.map((header) => (
                                  <option key={header} value={header}>
                                    {header}
                                  </option>
                                ))}
                                <option value={CUSTOM_FIELD_VALUE}>
                                  Custom Field
                                </option>
                              </select>
                              {mappings[field.key] &&
                                mappings[field.key] !== CUSTOM_FIELD_VALUE && (
                                  <input
                                    type="text"
                                    placeholder="Or type column name..."
                                    value={mappings[field.key] || ""}
                                    onChange={(e) => {
                                      const newValue = e.target.value;
                                      if (newValue.trim()) {
                                        setMappings((prev) => ({
                                          ...prev,
                                          [field.key]: newValue,
                                        }));
                                      }
                                    }}
                                    className="text-xs rounded border px-2 py-1 w-full min-w-[140px]"
                                    title="Edit the CSV column name if auto-mapping is incorrect"
                                  />
                                )}
                            </div>
                          )}
                          {mappings[field.key] && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => clearMapping(field.key)}
                              className="h-6 w-6 p-0"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Unmapped CSV Columns Section - columns from CSV that aren't mapped to any field */}
              {getUnmappedCsvColumns().length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide border-t pt-4">
                    Additional CSV Columns
                  </h4>
                  <p className="text-xs text-muted-foreground mb-2">
                    These columns from your CSV will be imported as custom data
                  </p>
                  {getUnmappedCsvColumns().map(({ fieldKey, header, target }) => {
                    const isMapped = mappings[fieldKey] === header;

                    return (
                      <div
                        key={fieldKey}
                        className="flex items-center justify-between p-2 rounded-lg border bg-card border-orange-200"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{header}</span>
                            <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700">
                              {isMapped ? "Will Import" : "CSV Column"}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Column from your CSV file
                          </p>
                          {isMapped && (
                            <p className="text-xs text-orange-600">
                              → {target}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              if (isMapped) {
                                // Unmap it
                                setMappings((prev) => {
                                  const newMappings = { ...prev };
                                  delete newMappings[fieldKey];
                                  return newMappings;
                                });
                              } else {
                                // Map it to import as custom field
                                setMappings((prev) => ({
                                  ...prev,
                                  [fieldKey]: header,
                                }));
                              }
                            }}
                            className={isMapped ? "text-xs bg-primary/10 text-primary border-primary/30 hover:bg-primary/20" : "text-xs"}
                          >
                            {isMapped ? "Don't Import" : "Import Column"}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="space-y-4">
          <h3 className="font-medium">
            Data Preview ({csvData?.length || 0} records)
          </h3>
          {previewData && previewData.length > 0 ? (
            <div className="border rounded-lg p-4 max-h-96 overflow-auto">
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="border-b">
                      {csvHeaders.map((header) => (
                        <th
                          key={header}
                          className="px-2 py-1 text-left font-medium"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((row, index) => (
                      <tr key={index} className="border-b">
                        {csvHeaders.map((header) => (
                          <td key={header} className="px-2 py-1">
                            {String(row[header] || "").substring(0, 50)}
                            {String(row[header] || "").length > 50 ? "..." : ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="border rounded-lg p-8 text-center text-muted-foreground">
              No data to preview
            </div>
          )}
        </div>
      </div>

      {/* Validation Warnings */}
      {missingRequired.length > 0 && (
        <Alert>
          <AlertDescription>
            <strong>Missing required fields:</strong>{" "}
            {missingRequired.map((f) => f.label).join(", ")}
          </AlertDescription>
        </Alert>
      )}

      {/* Import + Assign: choose the agent here instead of making a second trip
          to the assignment page after the import lands. */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <label htmlFor="import-assign-to" className="block text-sm font-semibold text-gray-900">
          Assign to
        </label>
        <p className="mt-0.5 text-xs text-gray-600">
          Optional. Everyone in this file goes straight to this agent, including
          people already in your leads, unless another agent has them. Leave it on{" "}
          <span className="font-medium">Nobody</span> to import unassigned.
        </p>
        {/* The SAME searchable picker the assignment page uses — not a second
            one. A plain <select> was unusable here: MRFG alone has 172 people,
            so it was a 172-item scroll with no search and no context. This one
            searches, shows initials, current assigned/uploaded counts and
            capacity, so you can tell two similarly-named agents apart — which is
            exactly the confusion that put 2,391 leads on the wrong person. */}
        <div className="mt-2 w-full max-w-sm">
          <AgentAssignmentDropdown
            value={assignToAgentId}
            onValueChange={setAssignToAgentId}
            placeholder="Nobody — import unassigned"
            openDirection="down"
            clearLabel="Nobody — import unassigned"
          />
        </div>
        {assignToAgentId && (
          <p className="mt-2 text-xs font-medium text-blue-700">
            Your choice overrides anything the file says about agents.
          </p>
        )}
      </div>

      {/*
        ── WHO WILL THIS FILE ASSIGN TO? ──────────────────────────────────────
        Shown BEFORE the import runs, because every failure in this flow so far
        has been silent: the import reported "completed" and put thousands of
        leads on the wrong agent with nothing on screen saying so.

        On 2026-09-01 nine vendor lists were imported in thirteen minutes. Each
        carried a blank `AGENT` column and the real name in `DriverIDFK`, so
        detection resolved nobody and the import fell back on a stale UI
        selection. One line of preview would have caught it on the second list.
      */}
      {/* No agent column = nothing to preview; the "Assign to" picker above covers that case. */}
      {agentPreview?.column ? (
        <Alert className={agentPreview.resolved.length === 0 ? "border-amber-300 bg-amber-50" : "border-blue-200 bg-blue-50"}>
          <AlertDescription className="text-sm">
            <div className="font-semibold text-gray-900">
              Agent column detected: <span className="font-mono">{agentPreview.column}</span>{" "}
              <span className="font-normal text-gray-600">
                ({agentPreview.filled.toLocaleString()} of {agentPreview.sampled.toLocaleString()} rows filled)
              </span>
            </div>
            {agentPreview.resolved.length > 0 ? (
              <ul className="mt-1.5 space-y-0.5">
                {agentPreview.resolved.map((r) => (
                  <li key={r.value}>
                    <span className="font-mono">{r.value}</span> →{" "}
                    {r.agentName ? (
                      <span className="font-semibold text-gray-900">{r.agentName}</span>
                    ) : (
                      <span className="font-semibold text-amber-800">
                        no matching agent — these will not be auto-assigned
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-1.5 text-amber-800">
                The column is empty, so nothing will be auto-assigned from this file.
              </div>
            )}
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button onClick={cancelImport} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Cancel
        </Button>
        <div className="flex items-center gap-3">
          <Button onClick={() => setCurrentStep("upload")} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={() => handleImport()}
            disabled={missingRequired.length > 0 || isPending || !!trashBusy}
            className="flex items-center gap-2"
          >
            {isPending || trashBusy ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-foreground" />
                {trashBusy ? "Checking..." : "Importing..."}
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Import {csvData?.length || 0} Leads
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Dupe-file confirmation — only shown when re-importing a file already imported */}
      <AlertDialog open={showDupConfirm} onOpenChange={setShowDupConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">
              You already imported this file
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                <span className="font-medium">&quot;{csvFile?.name}&quot;</span> was already imported
                {duplicateImportWarning && duplicateImportWarning.times > 1
                  ? ` ${duplicateImportWarning.times} times`
                  : ""}
                {duplicateImportWarning?.date
                  ? ` (last on ${new Date(
                      duplicateImportWarning.date,
                    ).toLocaleDateString()}, ${duplicateImportWarning.totalLeads.toLocaleString()} leads added)`
                  : ""}
                .
              </span>
              <span className="block">
                Rows that already exist are skipped automatically as duplicates, so you may
                add few or no new leads — but this upload is still kept as its own separate
                list. Import anyway?
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              onClick={() => {
                setShowDupConfirm(false);
                handleImport(true);
              }}
            >
              Import anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!trashMatches}
        onOpenChange={(open) => !open && trashBusy !== "deleting" && setTrashMatches(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {trashMatches?.rows === 1
                ? "1 of these leads is in Recently Deleted"
                : `${(trashMatches?.rows ?? 0).toLocaleString()} of these leads are in Recently Deleted`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Permanently delete {trashMatches?.rows === 1 ? "it" : "them"} to import this file. This can&apos;t be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={trashBusy === "deleting"}
              onClick={() => {
                setTrashMatches(null);
                cancelImport();
              }}
            >
              Cancel import
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              disabled={trashBusy === "deleting"}
              onClick={async (e) => {
                e.preventDefault(); // stays open until the delete lands
                if (!trashMatches) return;
                setTrashBusy("deleting");
                try {
                  await leadsService.permanentlyDeleteAllLeads(trashMatches.ids);
                } catch {
                  setTrashBusy(null); // the service already showed why
                  return;
                }
                void queryClient.invalidateQueries({ queryKey: leadsQueryKeys.deleted().slice(0, 2) });
                setTrashBusy(null);
                setTrashMatches(null);
                handleImport(true, true);
              }}
            >
              {trashBusy === "deleting" ? "Deleting..." : "Permanently delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MapImport;
